// src/app/pages/qr-compilations/qr-compilations.component.ts
import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';

import { QuizLayoutModule } from 'src/app/shared/components/quiz-layout/quiz-layout.module';

import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { GlobalService } from 'src/app/shared/global.service';
import { FireService } from 'src/app/services/fire.service';

import * as CryptoJS from 'crypto-js';

type QrPayload = {
  reparto?: string;
  id?: string;
  link?: string;
  url?: string;

  slot?: number;
  azienda?: string;
  licenza?: string;
  reparto_nome?: string;
  scadenza?: any; // Firestore Timestamp | string | Date
};

@Component({
  selector: 'app-qr-compilations',
  standalone: true,
  imports: [CommonModule, NzButtonModule, NzPopconfirmModule, QuizLayoutModule],
  templateUrl: './qr-compilations.component.html',
  styleUrls: ['./qr-compilations.component.scss']
})
export class QrCompilationsComponent implements OnInit, OnDestroy {
  // Data
  reparto = '';
  id = '';
  link = '';

  dueDateISO = '';
  dueDate = '';

  // UI flags
  isLoading = true;
  isStarting = false;

  showSuccess = false;
  showError = false;
  successText = '';
  errorText = '';

  // Business state
  isExpiredClima = false;

  scadenza: any = null;
  repartoName = '';
  slotFull = false;
  activeCompilation = false;

  slotReparto = 0;
  azienda = '';
  licenza = '';

  utente = '';
  nextUtente = '';
  idDevice = '';

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private g: GlobalService,
    private fire: FireService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.resetUi(true);

    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((pm: ParamMap) => {
        void this.handleRoute(pm.get('url') ?? '');
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // -------------------- Route handler --------------------
  private async handleRoute(urlParam: string): Promise<void> {
    this.resetUi(true);

    const enc = this.g.decryptData(urlParam);
    const payload = this.tryDecodeBase64Json(enc) as QrPayload | null;

    if (!payload) {
      this.fail('Link non valido: dati non leggibili.');
      return;
    }

    this.reparto = (payload.reparto ?? '').trim();
    this.id = (payload.id ?? '').trim();
    this.link = (payload.link ?? payload.url ?? '').trim();

    this.slotReparto = Number(payload.slot ?? 0);
    this.azienda = String(payload.azienda ?? '').trim();
    this.licenza = String(payload.licenza ?? '').trim();
    this.repartoName = String(payload.reparto_nome ?? '').trim();
    this.scadenza = payload.scadenza ?? null;

    this.dueDateISO = this.normalizeToISO(this.scadenza);
    this.dueDate = this.isoToItalian(this.dueDateISO);

    if (!this.reparto || !this.azienda || !this.licenza) {
      this.fail('Link incompleto: mancano azienda/licenza/reparto.');
      return;
    }

    if (this.isExpiredFromToday(this.scadenza)) {
      this.isExpiredClima = true;
      this.isLoading = false;
      return;
    }

    const stored = this.getDeviceId(this.azienda, this.licenza, this.reparto);
    const extractedUser = this.extractUserFromDeviceId(stored);

    if (extractedUser) {
      this.utente = this.nextUtente = extractedUser;
      await this.navigateToCompilazione();
      return;
    }

    this.activeCompilation = true;
    this.isLoading = false;
  }

  // -------------------- Action --------------------
  async useLicenza(): Promise<void> {
    this.isStarting = true;
    this.showError = false;
    this.showSuccess = false;

    try {

      // Allocazione atomica: niente race su scansioni QR concorrenti
      const { index: nextIndex, slotFull } =
        await this.fire.reserveNextUtenteIndexByReparto(
          this.azienda,
          this.licenza,
          this.reparto,
          this.slotReparto
        );

      if (slotFull) {
        this.slotFull = true;
        this.isStarting = false;
        return;
      }

      this.nextUtente = `U${nextIndex}`;

      const payload = {
        lang: 'it',
        reparto: this.reparto,
        sent: true,
        utente: this.nextUtente,
        dataInvio: new Date()
      };

      const docId = `${this.reparto}${this.nextUtente}`;

      await this.fire.addSubCollection(
        'clienti',
        'licenze',
        'compilazioni',
        payload,
        this.azienda,
        this.licenza,
        docId
      );

      let newUser = {
        admin: false,
        attivo: true,
        codice: Number(this.nextUtente.split('U')[1]),
        descrizione: this.nextUtente + ' [QR]',
        email: '-',
        mobile: '-',
        sendtype: false,
        id: this.nextUtente,
        lang: 'it',
        pwd: "",
        ultimo_accesso: {},
        reparto: this.reparto,
      };


      await this.fire.addSingleSubDoc(
        'clienti', 'reparti', 'utenti', this.azienda, this.reparto, newUser
      );


      await this.fire.incrementUtilizzate({ clienti: this.azienda, licenze: this.licenza }, 1);

      this.idDevice = this.createDeviceId(this.azienda, this.licenza, this.reparto, this.nextUtente);
      this.utente = this.nextUtente;

      await this.navigateToCompilazione();
    } catch {
      this.fail('Errore durante l’attivazione della licenza. Riprova tra poco.');
    } finally {
      this.isStarting = false;
    }
  }

  // -------------------- Navigation --------------------
  private async navigateToCompilazione(): Promise<void> {
    const customLink = {
      azienda: this.azienda,
      licenza: this.licenza,
      reparto: this.reparto,
      utente: this.nextUtente
    };

    const encryptLink = this.g.encryptData(
      CryptoJS.enc.Utf8.parse(JSON.stringify(customLink))
    );

    await this.router.navigateByUrl(`/compilazione/${encryptLink}`);
  }

  // -------------------- Expiry --------------------
  private isExpiredFromToday(scadenza: any): boolean {
    const d =
      scadenza?.seconds ? new Date(scadenza.seconds * 1000) :
        scadenza?.toDate ? scadenza.toDate() :
          new Date(scadenza);

    if (isNaN(d.getTime())) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);

    return d < today;
  }

  // -------------------- Base64 JSON decode --------------------
  private tryDecodeBase64Json(enc: string): any | null {
    try {
      const b64 = enc.replace(/-/g, '+').replace(/_/g, '/');
      const json = atob(b64);
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  // -------------------- Date helpers (for dueDate) --------------------
  private normalizeToISO(input: any): string {
    if (!input) return '';

    const dt =
      input?.seconds ? new Date(input.seconds * 1000) :
        input?.toDate ? input.toDate() :
          new Date(input);

    if (isNaN(dt.getTime())) return '';

    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private isoToItalian(iso: string): string {
    if (!iso) return '';
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return '';
    return `${m[3]}/${m[2]}/${m[1]}`;
  }

  // -------------------- DeviceId helpers --------------------
  private deviceKey(azienda: string, licenza: string, reparto: string): string {
    return `amajor-device-id_${azienda}_${licenza}_${reparto}`;
  }

  private getDeviceId(azienda: string, licenza: string, reparto: string): string {
    return localStorage.getItem(this.deviceKey(azienda, licenza, reparto)) ?? '';
  }

  private extractUserFromDeviceId(stored: string): string {
    if (!stored) return '';
    localStorage.removeItem(this.deviceKey(this.azienda, this.licenza, this.reparto));
    const parts = String(stored).split('_').filter(Boolean);

    const first = parts[0] ?? '';
    if (/^U\d+$/i.test(first)) return first.toUpperCase();

    const last = parts[parts.length - 1] ?? '';
    if (/^U\d+$/i.test(last)) return last.toUpperCase();

    return '';
  }

  private createDeviceId(azienda: string, licenza: string, reparto: string, utente: string): string {
    const uuid = crypto.randomUUID();
    localStorage.setItem(this.deviceKey(azienda, licenza, reparto), `${utente}_${uuid}`);
    return uuid;
  }

  // -------------------- UI helpers --------------------
  private resetUi(loading: boolean): void {
    this.isLoading = loading;
    this.isStarting = false;

    this.showSuccess = false;
    this.showError = false;
    this.successText = '';
    this.errorText = '';

    this.slotFull = false;
    this.isExpiredClima = false;
  }

  private fail(msg: string): void {
    this.isLoading = false;
    this.isStarting = false;
    this.showSuccess = false;
    this.showError = true;
    this.errorText = msg;
  }

  // optional
  private ok(msg: string): void {
    this.isLoading = false;
    this.showSuccess = true;
    this.showError = false;
    this.successText = msg;
  }
}
